// Builds Zod schemas for theme variables from the theme property registry in @repo/lib.
// `storage` is what the theme CRUD endpoints validate against; `apiInput` and `apiOutput` are the seller API's
// `themeOverrides`, whose values end up inside a <style> tag, so they only accept values that can't break out of it.

import {
  CUSTOM_CSS_MAX_LENGTH,
  describeThemePropertyForApi,
  THEME_OPTIONS,
  THEME_PROPERTY_REGISTRY,
  type OptionsRef,
  type ThemeProperty,
} from '@repo/lib';
import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export type ThemeVariablesSchemaMode = 'storage' | 'apiInput' | 'apiOutput';

/** `path` is relative to `variables.`, e.g. `tokens.colorPrimary`. */
export type ThemePathFilter = (path: string) => boolean;

const VARIABLES_PREFIX = 'variables.';

// Coerce string-encoded numbers (e.g. "80" → 80) that may exist in MongoDB
// from data stored before schema validation was added.
// Does NOT coerce empty string "" (used as "auto" sentinel) or non-numeric strings.
const coerceToNumber = (val: unknown) =>
  typeof val === 'string' && val !== '' && !isNaN(Number(val)) ? Number(val) : val;

function resolveOptionValues(options: ThemeProperty['options']): (string | number)[] | null {
  if (options == null) return null;

  if (typeof options === 'string') {
    const optionArray = THEME_OPTIONS[options as OptionsRef];
    if (!optionArray) return null;
    return optionArray.map(o => o.value);
  }

  return options.map(o => o.value);
}

// ============================================================================
// storage: the dashboard's theme create and update validation
// ============================================================================

const colorSchema = z
  .object({
    light: z.string().optional(),
    dark: z.string().optional(),
  })
  .nullable()
  .optional();

const imageSchema = z
  .object({
    file: z.string().optional(),
    url: z.string().optional(),
    name: z.string().optional(),
    size: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
  })
  .nullable()
  .optional();

function storageOptionValues(prop: ThemeProperty): (string | number)[] | null {
  const values = resolveOptionValues(prop.options);
  return values && prop.legacyValues ? [...values, ...prop.legacyValues] : values;
}

function storageSchemaForProperty(prop: ThemeProperty): z.ZodTypeAny {
  const { type } = prop;

  switch (type) {
    case 'color':
      return colorSchema;

    case 'font':
    case 'text':
      return z.string().nullable().optional();

    case 'fontWeight':
      return z.preprocess(coerceToNumber, z.number()).nullable().optional();

    case 'fontStyle': {
      const values = storageOptionValues(prop);
      if (values && values.length > 0) {
        return z
          .enum(values.map(String) as [string, ...string[]])
          .nullable()
          .optional();
      }
      return z.string().nullable().optional();
    }

    case 'fontSize':
      return z
        .preprocess(coerceToNumber, z.union([z.number(), z.literal('')]))
        .nullable()
        .optional();

    case 'size':
      return z
        .preprocess(coerceToNumber, z.union([z.number(), z.literal('')]))
        .nullable()
        .optional();

    case 'shadow': {
      const values = storageOptionValues(prop);
      if (values && values.length > 0) {
        return z
          .enum(values.map(String) as [string, ...string[]])
          .nullable()
          .optional();
      }
      return z.string().nullable().optional();
    }

    case 'alignment': {
      const values = storageOptionValues(prop);
      if (values && values.length > 0) {
        return z
          .enum(values.map(String) as [string, ...string[]])
          .nullable()
          .optional();
      }
      return z.string().nullable().optional();
    }

    case 'select': {
      const values = storageOptionValues(prop);
      if (values && values.length > 0) {
        const hasNumbers = values.some(v => typeof v === 'number');
        const hasStrings = values.some(v => typeof v === 'string');
        if (hasNumbers && hasStrings) {
          return z
            .preprocess(coerceToNumber, z.union([z.string(), z.number()]))
            .nullable()
            .optional();
        }
        if (hasNumbers) {
          return z.preprocess(coerceToNumber, z.number()).nullable().optional();
        }
        return z
          .enum(values.map(String) as [string, ...string[]])
          .nullable()
          .optional();
      }
      return z.union([z.string(), z.number()]).nullable().optional();
    }

    case 'toggle':
      return z.boolean().nullable().optional();

    case 'image':
      return imageSchema;

    default:
      return z.unknown().optional();
  }
}

// ============================================================================
// apiInput / apiOutput: the seller API's themeOverrides
// ============================================================================

// These land in a <style> tag, so the characters that could close a declaration, open or close a rule, or start a
// tag stay out. Any other value is the seller's to choose, including color functions CSS has not shipped yet.
const COLOR_STRING_PATTERN = /^[^;{}<>]+$/;
const FONT_FAMILY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ]{0,63}$/;
const NO_ANGLE_BRACKETS_PATTERN = /^[^<>]*$/;
const SELECT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const NUMBER_BOUNDS: Record<'size' | 'fontSize', { min: number; max: number }> = {
  size: { min: 0, max: 400 },
  fontSize: { min: 6, max: 200 },
};

// Registry entries of these types that declare no options get the shared option list.
const FALLBACK_OPTIONS: Partial<Record<ThemeProperty['type'], OptionsRef>> = {
  fontStyle: 'fontStyle',
  shadow: 'shadow',
  alignment: 'alignment',
};

// Messages below continue the seller API's error sentence, "Field '<path>' is <message, lowercased>".
// The color is a component of its own so the OpenAPI document carries the pattern once, not twice per color property.
const apiColorStringSchema = z
  .string()
  .max(256)
  .regex(COLOR_STRING_PATTERN, {
    message: 'Not a valid color: it cannot be empty or contain ; { } < >',
  })
  .meta({
    id: 'ThemeColorValue',
    description:
      "A CSS color value, such as `#1a2b3c`, `rgb(0 0 0 / 50%)`, `oklch(0.7 0.1 200)`, `color-mix(in srgb, red, blue)` or a color name. It cannot contain `;`, `{`, `}`, `<` or `>`. The API doesn't check that the value is a valid color: an invalid one is stored, and the browser ignores it on the page.",
  });

const apiColorSchema = z
  .object({
    light: apiColorStringSchema.nullable().optional(),
    dark: apiColorStringSchema.nullable().optional(),
  })
  .strict();

const apiFontSchema = z.string().regex(FONT_FAMILY_PATTERN, {
  message: 'Not a Google Fonts family name (letters, numbers and spaces, up to 64 characters)',
});

const apiTextSchema = z.string().max(500).regex(NO_ANGLE_BRACKETS_PATTERN, {
  message: 'Not allowed to contain < or >',
});

// Custom CSS is meant to carry CSS syntax, so it skips the text and color rules. The store renders it as the text of
// a <style> element, and a closing style tag is what would end that element and start markup. Spelled out case by
// case rather than with the i flag, which the OpenAPI pattern would lose.
const CUSTOM_CSS_PATH = 'variables.customCss';
const CUSTOM_CSS_PATTERN = /^(?![\s\S]*<\/[Ss][Tt][Yy][Ll][Ee])[^\u0000]*$/;

const apiCustomCssSchema = z.string().max(CUSTOM_CSS_MAX_LENGTH).regex(CUSTOM_CSS_PATTERN, {
  message: 'Not allowed to contain </style or null characters',
});

const ignoredImageInputFieldDescription =
  'Ignored on input: the server sets it from the uploaded file.';

// Takes the extra keys a read returns so an image can be sent back as is; only `file` is used.
const apiImageInputSchema = z
  .object({
    file: ObjectIdStringValidationSchema.meta({
      description:
        'ID of an image file you uploaded with the files API. The server fills in `url`, `name`, `size`, `width` and `height` from the file and ignores any values sent for them.',
    }),
    url: z.string().nullish().meta({ description: ignoredImageInputFieldDescription }),
    name: z.string().nullish().meta({ description: ignoredImageInputFieldDescription }),
    size: z.number().nullish().meta({ description: ignoredImageInputFieldDescription }),
    width: z.number().nullish().meta({ description: ignoredImageInputFieldDescription }),
    height: z.number().nullish().meta({ description: ignoredImageInputFieldDescription }),
  })
  .strict();

const apiImageOutputSchema = z.object({
  file: z.string().optional().meta({ description: 'ID of the image file.' }),
  url: z.string().optional(),
  name: z.string().optional(),
  size: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
});

function optionValuesSchema(values: (string | number)[]): z.ZodTypeAny {
  if (values.every(value => typeof value === 'string')) {
    return z.enum(values as [string, ...string[]]);
  }

  const literals = values.map(value => z.literal(value));
  const union =
    literals.length === 1
      ? literals[0]
      : z.union(literals as [z.ZodLiteral, z.ZodLiteral, ...z.ZodLiteral[]]);
  // The union alone fails with a bare "Invalid input", so report a miss as z.enum does, with every option.
  // The union stays for the OpenAPI document, which renders a multi-value z.literal as its first value only.
  return z.preprocess((value, ctx) => {
    const coerced = coerceToNumber(value);
    if (!values.includes(coerced as string | number)) {
      ctx.issues.push({ code: 'invalid_value', values, input: value });
    }
    return coerced;
  }, union);
}

/** The values the seller API accepts for a theme property limited to a list, or null when it takes a free value. */
export function getThemePropertyApiOptionValues(prop: ThemeProperty): (string | number)[] | null {
  const values = resolveOptionValues(prop.options);
  if (values && values.length > 0) return values;

  const fallback = FALLBACK_OPTIONS[prop.type];
  return fallback ? THEME_OPTIONS[fallback].map(o => o.value) : null;
}

function apiValueSchemaForProperty(
  prop: ThemeProperty,
  mode: Exclude<ThemeVariablesSchemaMode, 'storage'>,
): z.ZodTypeAny {
  switch (prop.type) {
    case 'color':
      return apiColorSchema;

    case 'font':
      return apiFontSchema;

    case 'text':
      return prop.path === CUSTOM_CSS_PATH ? apiCustomCssSchema : apiTextSchema;

    case 'toggle':
      return z.boolean();

    case 'image':
      return mode === 'apiInput' ? apiImageInputSchema : apiImageOutputSchema;

    case 'fontWeight':
    case 'fontSize':
    case 'size': {
      const values = resolveOptionValues(prop.options);
      if (values && values.length > 0) return optionValuesSchema(values);

      if (prop.type === 'fontWeight') {
        return z.preprocess(coerceToNumber, z.number().int().min(100).max(900).multipleOf(100));
      }

      const { min, max } = NUMBER_BOUNDS[prop.type];
      return z.preprocess(coerceToNumber, z.number().min(min).max(max));
    }

    case 'select':
    case 'shadow':
    case 'alignment':
    case 'fontStyle': {
      const values = getThemePropertyApiOptionValues(prop);
      return values ? optionValuesSchema(values) : z.string().regex(SELECT_TOKEN_PATTERN);
    }

    default:
      return z.never();
  }
}

function formatDefault(value: unknown): string {
  return typeof value === 'string' ? JSON.stringify(value) : String(value);
}

function describeProperty(prop: ThemeProperty): string {
  const description = describeThemePropertyForApi(prop);
  if (prop.defaultValue === undefined) return description;

  const sentence = /[.!?]$/.test(description) ? description : `${description}.`;
  return `${sentence} Default: ${formatDefault(prop.defaultValue)}.`;
}

export function buildApiLeafSchema(
  prop: ThemeProperty,
  mode: Exclude<ThemeVariablesSchemaMode, 'storage'>,
) {
  return apiValueSchemaForProperty(prop, mode)
    .nullable()
    .optional()
    .meta({ description: describeProperty(prop) });
}

// ============================================================================
// Tree
// ============================================================================

interface TreeNode {
  children: Map<string, TreeNode>;
  schema?: z.ZodTypeAny;
}

function buildTree(
  entries: ThemeProperty[],
  leafSchema: (prop: ThemeProperty) => z.ZodTypeAny,
): TreeNode {
  const root: TreeNode = { children: new Map() };

  for (const entry of entries) {
    const parts = entry.path.slice(VARIABLES_PREFIX.length).split('.');

    let current = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current.children.has(part)) {
        current.children.set(part, { children: new Map() });
      }
      current = current.children.get(part)!;
    }

    const leafKey = parts[parts.length - 1];
    if (!current.children.has(leafKey)) {
      current.children.set(leafKey, { children: new Map() });
    }
    current.children.get(leafKey)!.schema = leafSchema(entry);
  }

  return root;
}

function nodeShape(
  node: TreeNode,
  objectFor: (shape: Record<string, z.ZodTypeAny>) => z.ZodTypeAny,
) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const [key, child] of node.children) {
    if (child.children.size > 0) {
      shape[key] = objectFor(nodeShape(child, objectFor));
    } else if (child.schema) {
      shape[key] = child.schema;
    }
  }

  return shape;
}

type ThemeVariablesStorageSchema = z.ZodOptional<
  z.ZodNullable<z.ZodObject<Record<string, z.ZodTypeAny>>>
>;

export function buildThemeVariablesSchema(options: {
  mode: 'storage';
  passthrough: boolean;
  include?: ThemePathFilter;
}): ThemeVariablesStorageSchema;
export function buildThemeVariablesSchema(options: {
  mode: 'apiInput' | 'apiOutput';
  include?: ThemePathFilter;
}): z.ZodObject<Record<string, z.ZodTypeAny>, z.core.$strict>;
export function buildThemeVariablesSchema(options: {
  mode: ThemeVariablesSchemaMode;
  passthrough?: boolean;
  include?: ThemePathFilter;
}): z.ZodTypeAny {
  const { mode, include } = options;
  const entries = THEME_PROPERTY_REGISTRY.filter(
    entry =>
      entry.path.startsWith(VARIABLES_PREFIX) &&
      (!include || include(entry.path.slice(VARIABLES_PREFIX.length))),
  );

  if (mode === 'storage') {
    const passthrough = options.passthrough ?? false;
    const shape = nodeShape(buildTree(entries, storageSchemaForProperty), childShape => {
      const obj = z.object(childShape);
      return (passthrough ? obj.passthrough() : obj.strict()).nullable().optional();
    });
    const obj = z.object(shape);
    return passthrough
      ? obj.passthrough().nullable().optional()
      : obj.strict().nullable().optional();
  }

  const shape = nodeShape(
    buildTree(entries, prop => buildApiLeafSchema(prop, mode)),
    childShape => z.object(childShape).strict().nullable().optional(),
  );
  return z.object(shape).strict();
}
